import React, { useEffect, useState } from 'react';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase';

const STAGE_COLORS = {
  'Problem-Solution Fit':        '#1d4ed8',
  'Product-Market Fit':          '#0369a1',
  'Early Growth & Monetization': '#166534',
  'Scaling & Regional Expansion':'#065f46',
};

export default function CoachPerformance({ coachId, coachName, showAI = true, scopedCompanies }) {
  const [companies, setCompanies]     = useState([]);
  const [allSessions, setAllSessions] = useState([]);
  const [allWorkPlans, setAllWorkPlans] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [expanded, setExpanded]       = useState(false);
  const [loadError, setLoadError]     = useState(null);

  useEffect(() => {
    if (!coachId) return;
    setLoading(true);
    setLoadError(null);
    async function load() {
      try {
        // When scopedCompanies is provided (e.g. by the Client dashboard, which cannot
        // safely run an unscoped 'companies' query under our per-document security rules),
        // filter from that pre-loaded, already-permitted list instead of querying independently.
        const comps = scopedCompanies
          ? scopedCompanies.filter(c => c.coachId === coachId)
          : (await getDocs(query(collection(db, 'companies'), where('coachId', '==', coachId))))
              .docs.map(d => ({ id: d.id, ...d.data() }));
        setCompanies(comps);

        const sessions  = [];
        const workPlans = [];

        for (const comp of comps) {
          const sSnap = await getDocs(
            query(collection(db, 'companies', comp.id, 'coachingSessions'), orderBy('sessionDate', 'desc'))
          );
          sSnap.docs.forEach(d => sessions.push({ ...d.data(), companyId: comp.id, companyName: comp.name }));

          const wpSnap = await getDocs(collection(db, 'companies', comp.id, 'workPlans'));
          wpSnap.docs.forEach(d => workPlans.push({ ...d.data(), companyId: comp.id, companyName: comp.name }));
        }

        setAllSessions(sessions);
        setAllWorkPlans(workPlans);
      } catch (err) {
        console.error('CoachPerformance load error for coachId', coachId, ':', err);
        setLoadError(err.message || 'Failed to load coach data');
      } finally { setLoading(false); }
    }
    load();
  }, [coachId, scopedCompanies]);

  // ── Metrics ───────────────────────────────────────────────
  const totalSessions   = allSessions.length;
  const totalActions    = allSessions.flatMap(s => s.actionItems || []).length;
  const openActions     = allSessions.flatMap(s => (s.actionItems||[]).filter(a => a.status === 'open')).length;
  const completedActions = totalActions - openActions;

  const workPlansByStatus = {
    draft:    allWorkPlans.filter(w => w.status === 'draft' || !w.status).length,
    agreed:   allWorkPlans.filter(w => w.status === 'agreed').length,
    reported: allWorkPlans.filter(w => w.status === 'reported').length,
    reviewed: allWorkPlans.filter(w => w.status === 'reviewed').length,
  };

  // Sessions per company
  const sessionsByCompany = companies.map(c => ({
    ...c,
    sessionCount: allSessions.filter(s => s.companyId === c.id).length,
    lastSession: allSessions.filter(s => s.companyId === c.id)[0]?.sessionDate,
  }));

  // Average sessions per company
  const avgSessions = companies.length > 0
    ? (totalSessions / companies.length).toFixed(1)
    : 0;

  const formatDate = (ts) => {
    if (!ts) return '—';
    const d = ts?.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  if (loading) return <div className="gm-loading"><div className="gm-spinner" /></div>;
  if (loadError) return (
    <div className="gm-coach-perf">
      <div className="gm-coach-perf-header">
        <div className="gm-coach-perf-name">
          <span className="gm-coach-perf-avatar">{coachName?.charAt(0)}</span>
          <div>
            <div className="gm-coach-perf-title">{coachName}</div>
            <div className="gm-coach-perf-sub" style={{ color: '#dc2626' }}>Error loading data: {loadError}</div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="gm-coach-perf">
      {/* Header */}
      <div className="gm-coach-perf-header" onClick={() => setExpanded(!expanded)}>
        <div className="gm-coach-perf-name">
          <span className="gm-coach-perf-avatar">{coachName?.charAt(0)}</span>
          <div>
            <div className="gm-coach-perf-title">{coachName}</div>
            <div className="gm-coach-perf-sub">{companies.length} companies · {totalSessions} sessions</div>
          </div>
        </div>
        <div className="gm-coach-perf-metrics">
          <div className="gm-coach-perf-metric">
            <div className="gm-coach-perf-metric-value">{totalSessions}</div>
            <div className="gm-coach-perf-metric-label">Sessions</div>
          </div>
          <div className="gm-coach-perf-metric">
            <div className="gm-coach-perf-metric-value">{avgSessions}</div>
            <div className="gm-coach-perf-metric-label">Avg/company</div>
          </div>
          <div className="gm-coach-perf-metric">
            <div className="gm-coach-perf-metric-value" style={{ color: openActions > 0 ? '#dc2626' : '#166534' }}>{openActions}</div>
            <div className="gm-coach-perf-metric-label">Open actions</div>
          </div>
          <div className="gm-coach-perf-metric">
            <div className="gm-coach-perf-metric-value">{workPlansByStatus.agreed + workPlansByStatus.reported + workPlansByStatus.reviewed}</div>
            <div className="gm-coach-perf-metric-label">Plans agreed</div>
          </div>
          <div className="gm-coach-perf-metric">
            <div className="gm-coach-perf-metric-value">{workPlansByStatus.reviewed}</div>
            <div className="gm-coach-perf-metric-label">Plans reviewed</div>
          </div>
        </div>
        <span className="gm-session-toggle">{expanded ? '▲' : '▼'}</span>
      </div>

      {expanded && (
        <div className="gm-coach-perf-body">
          {/* Work plan pipeline */}
          <div className="gm-coach-perf-section">
            <div className="gm-coach-perf-section-title">Work plan pipeline</div>
            <div className="gm-coach-perf-pipeline">
              {[
                { label: 'Draft', count: workPlansByStatus.draft, color: '#6b6b6b' },
                { label: 'Agreed', count: workPlansByStatus.agreed, color: '#0369a1' },
                { label: 'Reported', count: workPlansByStatus.reported, color: '#166534' },
                { label: 'Reviewed', count: workPlansByStatus.reviewed, color: '#065f46' },
              ].map(({ label, count, color }) => (
                <div key={label} className="gm-coach-perf-pipeline-item">
                  <div className="gm-coach-perf-pipeline-count" style={{ color }}>{count}</div>
                  <div className="gm-coach-perf-pipeline-label">{label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Action items */}
          <div className="gm-coach-perf-section">
            <div className="gm-coach-perf-section-title">Action items</div>
            <div className="gm-coach-perf-pipeline">
              <div className="gm-coach-perf-pipeline-item">
                <div className="gm-coach-perf-pipeline-count">{totalActions}</div>
                <div className="gm-coach-perf-pipeline-label">Total</div>
              </div>
              <div className="gm-coach-perf-pipeline-item">
                <div className="gm-coach-perf-pipeline-count" style={{ color: '#166534' }}>{completedActions}</div>
                <div className="gm-coach-perf-pipeline-label">Completed</div>
              </div>
              <div className="gm-coach-perf-pipeline-item">
                <div className="gm-coach-perf-pipeline-count" style={{ color: openActions > 0 ? '#dc2626' : '#166534' }}>{openActions}</div>
                <div className="gm-coach-perf-pipeline-label">Open</div>
              </div>
            </div>
          </div>

          {/* Per company breakdown */}
          <div className="gm-coach-perf-section">
            <div className="gm-coach-perf-section-title">Company breakdown</div>
            <table className="gm-table">
              <thead>
                <tr>
                  <th>Company</th>
                  <th>Stage</th>
                  <th>Sessions</th>
                  <th>Last session</th>
                  <th>Open actions</th>
                </tr>
              </thead>
              <tbody>
                {sessionsByCompany.map(c => {
                  const compOpenActions = allSessions
                    .filter(s => s.companyId === c.id)
                    .flatMap(s => (s.actionItems||[]).filter(a => a.status === 'open')).length;
                  const color = STAGE_COLORS[c.currentStage] || '#4a5481';
                  return (
                    <tr key={c.id}>
                      <td style={{ fontWeight: 600 }}>{c.name}</td>
                      <td><span style={{ color, fontSize: '0.78rem', fontWeight: 600 }}>{c.currentStage}</span></td>
                      <td>{c.sessionCount}</td>
                      <td style={{ fontSize: '0.78rem' }}>{formatDate(c.lastSession)}</td>
                      <td>
                        {compOpenActions > 0
                          ? <span style={{ color: '#dc2626', fontWeight: 700 }}>{compOpenActions}</span>
                          : <span style={{ color: '#166534' }}>✓</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* AI Summary */}
          {showAI && companies.length > 0 && (
            <div className="gm-coach-perf-section">
              <div className="gm-coach-perf-section-title">AI coaching summary</div>
              <CoachAISummary coachId={coachId} coachName={coachName} companies={companies} sessions={allSessions} workPlans={allWorkPlans} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Coach-specific AI summary ────────────────────────────────
function CoachAISummary({ coachId, coachName, companies, sessions, workPlans }) {
  const [summary, setSummary]       = useState(null);
  const [generating, setGenerating] = useState(false);
  const [error, setError]           = useState(null);
  const generate = async () => {
    setGenerating(true);
    setError(null);
    try {
      const totalSessions  = sessions.length;
      const openActions    = sessions.flatMap(s => (s.actionItems||[]).filter(a => a.status === 'open')).length;
      const reviewedPlans  = workPlans.filter(w => w.status === 'reviewed').length;
      const avgSessions    = companies.length > 0 ? (totalSessions / companies.length).toFixed(1) : 0;

      const companySummary = companies.map(c =>
        '- ' + c.name + ': ' + c.currentStage + ' (' + c.currentStageUpgrade + ', ' + c.currentStageProgress + '% through stage) → Target: ' + c.targetStage
      ).join('\n');

      const sessionSummary = sessions.slice(0,5).map(s =>
        '- ' + s.companyName + ': ' + (s.keyPoints?.slice(0,100)||'No notes')
      ).join('\n');

      const prompt = 'You are a programme analyst reviewing a coach performance in an enterprise scaling programme.\n\n' +
        'Coach: ' + coachName + '\n' +
        'Assigned companies: ' + companies.length + '\n' +
        'Total sessions held: ' + totalSessions + ' (avg ' + avgSessions + ' per company)\n' +
        'Open action items: ' + openActions + '\n' +
        'Work plans fully reviewed: ' + reviewedPlans + ' of ' + workPlans.length + '\n\n' +
        'Companies and stages:\n' + companySummary + '\n\n' +
        'Recent session topics (last 5):\n' + sessionSummary + '\n\n' +
        'Write a concise 2-3 paragraph coaching performance summary covering: ' +
        '1. Overall coaching cadence and engagement level. ' +
        '2. Portfolio progress — which companies are advancing well and which need more attention. ' +
        '3. Key areas to focus on in the coming weeks. ' +
        'Write professionally, constructively, and specifically. Do not fabricate data not provided.';

      const fn = httpsCallable(functions, 'generateAISummary');
      const result = await fn({ prompt, companyId: 'coach_' + coachId });
      setSummary(result.data?.summary || 'Summary generation failed.');
    } catch (err) {
      console.error(err);
      setError('Unable to generate summary.');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="gm-ai-summary">
      <div className="gm-ai-summary-header">
        <div className="gm-ai-summary-title">
          <span className="gm-ai-badge">AI</span>
          Coaching summary
        </div>
        <button className="gm-btn gm-btn-sm gm-btn-outline" onClick={generate} disabled={generating}>
          {generating ? 'Generating…' : '↻ Refresh'}
        </button>
      </div>
      {generating && <div className="gm-ai-summary-loading"><div className="gm-spinner" /><span>Generating…</span></div>}
      {error && <div className="gm-alert gm-alert-error" style={{ margin: 12 }}>{error}</div>}
      {summary && !generating && (
        <div className="gm-ai-summary-body">
          {summary.split('\n\n').map((p, i) => <p key={i}>{p}</p>)}
        </div>
      )}
      {!summary && !generating && !error && (
        <div className="gm-ai-summary-loading" style={{ color: '#94a3b8', fontSize: '0.82rem' }}>
          Preparing coaching summary…
        </div>
      )}
    </div>
  );
}
